/**
 * Central configuration for all disciplines offered at Rhythm & Beats
 * This is the single source of truth for discipline names and related data
 */

export interface Discipline {
  id: string;           // Unique identifier (camelCase)
  name: string;         // Display name
  category: 'music' | 'dance' | 'wellness';
  icon?: string;        // Optional emoji or icon identifier
}

/**
 * All disciplines offered at Rhythm & Beats
 */
export const DISCIPLINES: Discipline[] = [
  // Music - Instruments
  { id: 'guitar', name: 'Guitar', category: 'music', icon: '🎸' },
  { id: 'ukulele', name: 'Ukulele', category: 'music', icon: '🎸' },
  { id: 'violin', name: 'Violin (Carnatic)', category: 'music', icon: '🎻' },
  { id: 'pianoKeyboard', name: 'Piano/Keyboard', category: 'music', icon: '🎹' },
  { id: 'drums', name: 'Drums', category: 'music', icon: '🥁' },
  { id: 'flute', name: 'Flute', category: 'music', icon: '🎵' },

  // Music - Vocals
  { id: 'vocalsWestern', name: 'Vocals (Western)', category: 'music', icon: '🎤' },
  { id: 'vocalsHindustani', name: 'Vocals (Hindustani)', category: 'music', icon: '🎤' },
  { id: 'vocalsCarnatic', name: 'Vocals (Carnatic)', category: 'music', icon: '🎤' },

  // Dance - Classical
  { id: 'bharatanatyam', name: 'Bharatanatyam', category: 'dance', icon: '💃' },
  { id: 'kuchipudi', name: 'Kuchipudi', category: 'dance', icon: '💃' },
  { id: 'mohiniyattam', name: 'Mohiniyattam', category: 'dance', icon: '💃' },
  { id: 'kathak', name: 'Kathak', category: 'dance', icon: '💃' },

  // Dance - Modern
  { id: 'latinDance', name: 'Bachata (Latin Dance)', category: 'dance', icon: '🕺' },
  { id: 'freestyleDance', name: 'Freestyle Dance', category: 'dance', icon: '🕺' },

  // Wellness
  { id: 'yoga', name: 'Yoga & Fitness', category: 'wellness', icon: '🧘' },
];

/**
 * Get all discipline names as a simple array
 */
export const getDisciplineNames = (): string[] => {
  return DISCIPLINES.map(d => d.name);
};

/**
 * Get discipline names by category
 */
export const getDisciplinesByCategory = (category: 'music' | 'dance' | 'wellness'): string[] => {
  return DISCIPLINES.filter(d => d.category === category).map(d => d.name);
};

/**
 * Get enquiry field name for a discipline
 * e.g., 'guitar' -> 'enquiriesGuitar'
 */
export const getEnquiryFieldName = (disciplineId: string): string => {
  return `enquiries${disciplineId.charAt(0).toUpperCase()}${disciplineId.slice(1)}`;
};

/**
 * Get all enquiry field names
 */
export const getAllEnquiryFieldNames = (): string[] => {
  return DISCIPLINES.map(d => getEnquiryFieldName(d.id));
};

/**
 * Generate mock enquiry data for testing
 * Returns an object with enquiry field names as keys and random numbers (1-5) as values
 * Only includes disciplines that have enquiries (no zeros), some disciplines randomly skipped
 */
export const generateMockEnquiryData = (): Record<string, number> => {
  const mockData: Record<string, number> = {};
  DISCIPLINES.forEach(discipline => {
    // Randomly decide if this discipline has enquiries (70% chance)
    if (Math.random() > 0.3) {
      const fieldName = getEnquiryFieldName(discipline.id);
      // Generate random number between 1 and 5 (no zeros)
      mockData[fieldName] = Math.floor(Math.random() * 5) + 1;
    }
  });
  return mockData;
};

/**
 * Generate mock enquiry data and return both data and selected field names
 * This ensures the selected disciplines match the ones with actual values
 */
export const generateMockEnquiryDataWithSelection = (): {
  data: Record<string, number>;
  selectedFields: string[];
} => {
  const data: Record<string, number> = {};
  const selectedFields: string[] = [];

  DISCIPLINES.forEach(discipline => {
    // Randomly decide if this discipline has enquiries (70% chance)
    if (Math.random() > 0.3) {
      const fieldName = getEnquiryFieldName(discipline.id);
      // Generate random number between 1 and 5 (no zeros)
      data[fieldName] = Math.floor(Math.random() * 5) + 1;
      selectedFields.push(fieldName);
    }
  });

  return { data, selectedFields };
};

/**
 * Generate enquiry fields configuration for admin forms
 */
export const generateEnquiryFields = () => {
  return DISCIPLINES.map(discipline => ({
    name: getEnquiryFieldName(discipline.id),
    label: discipline.name,
    type: 'number' as const,
    required: false,
    placeholder: '0',
    isEnquiry: true
  }));
};

/**
 * Student levels - categorized by discipline type
 */

// RSL/Trinity graded disciplines (Western instruments and vocals)
export const RSL_TRINITY_LEVELS = [
  'Beginner',
  'Debut',
  'Grade 1',
  'Grade 2',
  'Grade 3',
  'Grade 4',
  'Grade 5',
  'Grade 6',
  'Grade 7',
  'Grade 8'
] as const;

// Dance levels (Latin and Freestyle)
export const DANCE_LEVELS = [
  'Beginner',
  'Intermediate',
  'Advanced'
] as const;

// Classical/Indian disciplines levels (Hindustani, Carnatic, Kathak, Bharatanatyam)
export const CLASSICAL_INDIAN_LEVELS = [
  'Beginner (Prarambhik)',
  'Intermediate (Praveshika Pratham/Poorna)',
  'Advanced (Madhyama Pratham/Poorna Pre-Arangetram)',
  'Diploma (Visharad/Arangetram/Alankar)'
] as const;

// Legacy/default levels
export const STUDENT_LEVELS = ['Beginner', 'Intermediate', 'Advanced'] as const;
export type StudentLevel = typeof STUDENT_LEVELS[number];

// RSL/Trinity discipline IDs
export const RSL_TRINITY_DISCIPLINES = [
  'guitar',
  'ukulele',
  'pianoKeyboard',
  'drums',
  'vocalsWestern'
] as const;

// Dance discipline IDs
export const DANCE_DISCIPLINE_IDS = [
  'latinDance',
  'freestyleDance'
] as const;

// Classical/Indian discipline IDs
export const CLASSICAL_INDIAN_DISCIPLINES = [
  'vocalsHindustani',
  'vocalsCarnatic',
  'kathak',
  'bharatanatyam',
  'violin' // Carnatic Violin
] as const;

/**
 * Get levels for a specific discipline
 * @param disciplineId - The discipline ID or name
 * @returns Array of level options for the discipline
 */
export const getLevelsForDiscipline = (disciplineId: string): string[] => {
  // Normalize the discipline identifier (handle both ID and name)
  const normalizedId = disciplineId.toLowerCase().replace(/[^a-z]/g, '');

  // Check RSL/Trinity disciplines
  if (RSL_TRINITY_DISCIPLINES.some(d => normalizedId.includes(d.toLowerCase()) ||
      normalizedId === 'guitar' ||
      normalizedId === 'ukulele' ||
      normalizedId.includes('keyboard') ||
      normalizedId.includes('piano') ||
      normalizedId === 'drums' ||
      (normalizedId.includes('vocal') && normalizedId.includes('western')))) {
    return [...RSL_TRINITY_LEVELS];
  }

  // Check Dance disciplines
  if (DANCE_DISCIPLINE_IDS.some(d => normalizedId.includes(d.toLowerCase())) ||
      normalizedId.includes('latin') ||
      normalizedId.includes('freestyle') ||
      normalizedId.includes('bachata')) {
    return [...DANCE_LEVELS];
  }

  // Check Classical/Indian disciplines
  if (CLASSICAL_INDIAN_DISCIPLINES.some(d => normalizedId.includes(d.toLowerCase())) ||
      normalizedId.includes('hindustani') ||
      normalizedId.includes('carnatic') ||
      normalizedId.includes('kathak') ||
      normalizedId.includes('bharatanatyam') ||
      normalizedId.includes('bharatnatyam') ||
      normalizedId.includes('kuchipudi') ||
      normalizedId.includes('mohiniyattam') ||
      (normalizedId.includes('violin') && !normalizedId.includes('western'))) {
    return [...CLASSICAL_INDIAN_LEVELS];
  }

  // Default levels for any other discipline
  return [...STUDENT_LEVELS];
};

/**
 * Get the discipline category for level mapping
 * @param disciplineId - The discipline ID or name
 * @returns The category name
 */
export const getDisciplineCategory = (disciplineId: string): 'rsl-trinity' | 'dance' | 'classical-indian' | 'default' => {
  const normalizedId = disciplineId.toLowerCase().replace(/[^a-z]/g, '');

  if (RSL_TRINITY_DISCIPLINES.some(d => normalizedId.includes(d.toLowerCase())) ||
      normalizedId === 'guitar' ||
      normalizedId === 'ukulele' ||
      normalizedId.includes('keyboard') ||
      normalizedId.includes('piano') ||
      normalizedId === 'drums' ||
      (normalizedId.includes('vocal') && normalizedId.includes('western'))) {
    return 'rsl-trinity';
  }

  if (DANCE_DISCIPLINE_IDS.some(d => normalizedId.includes(d.toLowerCase())) ||
      normalizedId.includes('latin') ||
      normalizedId.includes('freestyle') ||
      normalizedId.includes('bachata')) {
    return 'dance';
  }

  if (CLASSICAL_INDIAN_DISCIPLINES.some(d => normalizedId.includes(d.toLowerCase())) ||
      normalizedId.includes('hindustani') ||
      normalizedId.includes('carnatic') ||
      normalizedId.includes('kathak') ||
      normalizedId.includes('bharatanatyam') ||
      normalizedId.includes('bharatnatyam') ||
      (normalizedId.includes('violin') && !normalizedId.includes('western'))) {
    return 'classical-indian';
  }

  return 'default';
};

/**
 * Export discipline names as a constant array for use in select options
 */
export const DISCIPLINE_OPTIONS = getDisciplineNames();

